/**
 * Kleine DOM-Helfer. Ersetzt ein UI-Framework: die App ist klein genug,
 * dass Elemente von Hand zu bauen guenstiger ist als eine Abhaengigkeit.
 */

type EventName = keyof HTMLElementEventMap;

export interface ElProps {
  class?: string;
  text?: string;
  html?: string;
  /** Wird als data-* gesetzt. */
  data?: Record<string, string>;
  /** Inline-Styles, z. B. { width: '40px' }. */
  style?: Partial<CSSStyleDeclaration>;
  /** Alles andere wird als Attribut gesetzt (aria-*, type, value, ...). */
  attrs?: Record<string, string | number | boolean | null | undefined>;
  /** Ereignisse, z. B. { click: () => ... }. */
  on?: { [K in EventName]?: (ev: HTMLElementEventMap[K]) => void };
}

export type Child = Node | string | number | null | undefined | false;

/** Erzeugt ein Element mit Eigenschaften und Kindern. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props.class) el.className = props.class;
  if (props.text !== undefined) el.textContent = props.text;
  if (props.html !== undefined) el.innerHTML = props.html;
  if (props.data) for (const [k, v] of Object.entries(props.data)) el.dataset[k] = v;
  if (props.style) Object.assign(el.style, props.style);
  if (props.attrs) {
    for (const [k, v] of Object.entries(props.attrs)) {
      if (v === null || v === undefined || v === false) continue;
      el.setAttribute(k, String(v));
    }
  }
  if (props.on) {
    for (const [name, handler] of Object.entries(props.on)) {
      el.addEventListener(name, handler as EventListener);
    }
  }
  append(el, children);
  return el;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Kurzform fuer einen Button. */
export function button(
  label: string,
  onClick: () => void,
  opts: { class?: string; title?: string; pressed?: boolean } = {},
): HTMLButtonElement {
  return h('button', {
    class: opts.class ?? 'btn',
    text: label,
    attrs: {
      type: 'button',
      title: opts.title ?? null,
      'aria-label': opts.title ?? null,
      'aria-pressed': opts.pressed === undefined ? null : String(opts.pressed),
    },
    on: { click: onClick },
  });
}

/** Beschriftetes Eingabefeld. */
export function field(
  label: string,
  input: HTMLElement,
  hint?: string,
): HTMLElement {
  return h(
    'label',
    { class: 'field' },
    h('span', { class: 'field__label', text: label }),
    input,
    hint ? h('p', { class: 'hint', text: hint }) : null,
  );
}

/** Text-Eingabefeld mit iPadOS-Scribble-freundlichen Einstellungen. */
export function textInput(opts: {
  value?: string;
  placeholder?: string;
  type?: string;
  inputMode?: string;
  onInput?: (value: string, el: HTMLInputElement) => void;
}): HTMLInputElement {
  const el = h('input', {
    class: 'input',
    attrs: {
      type: opts.type ?? 'text',
      placeholder: opts.placeholder ?? '',
      inputmode: opts.inputMode ?? null,
      // Scribble braucht ein normales Eingabefeld ohne Autokorrektur-Tricks.
      autocapitalize: 'sentences',
      autocomplete: 'off',
      autocorrect: 'off',
      spellcheck: 'false',
      enterkeyhint: 'done',
    },
  });
  el.value = opts.value ?? '';
  if (opts.onInput) el.addEventListener('input', () => opts.onInput?.(el.value, el));
  return el;
}

/** Mehrzeiliges Feld, ebenfalls Scribble-tauglich. */
export function textArea(opts: {
  value?: string;
  placeholder?: string;
  rows?: number;
  class?: string;
  onInput?: (value: string, el: HTMLTextAreaElement) => void;
}): HTMLTextAreaElement {
  const el = h('textarea', {
    class: opts.class ?? 'textarea',
    attrs: {
      rows: opts.rows ?? 4,
      placeholder: opts.placeholder ?? '',
      autocapitalize: 'sentences',
      autocomplete: 'off',
      autocorrect: 'off',
      spellcheck: 'false',
    },
  });
  el.value = opts.value ?? '';
  if (opts.onInput) el.addEventListener('input', () => opts.onInput?.(el.value, el));
  return el;
}

/** Auswahlfeld. */
export function select(
  options: { value: string; label: string }[],
  value: string,
  onChange: (value: string) => void,
): HTMLSelectElement {
  const el = h('select', { class: 'select' });
  for (const opt of options) {
    el.append(h('option', { text: opt.label, attrs: { value: opt.value } }));
  }
  el.value = value;
  el.addEventListener('change', () => onChange(el.value));
  return el;
}

/** Entfernt alle Kinder eines Elements. */
export function clear(el: Element): void {
  el.textContent = '';
}
